import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If we're processing OAuth callback, skip the /me check (handled by AuthCallback)
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip /me call if we don't have any auth artifact yet (avoids 401 noise on first visit)
    // Le cookie access_token est httpOnly (illisible en JS) : on utilise un simple marqueur local
    const hasToken = typeof window !== "undefined" && (
      localStorage.getItem("fripri_logged_in") ||
      localStorage.getItem("auth_token") ||
      window.location.search.includes("apple=success") ||
      document.cookie.includes("session_token=")
    );
    if (!hasToken) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Migration : on ne conserve plus jamais le token en localStorage (cookie httpOnly uniquement)
      localStorage.setItem("fripri_logged_in", "1");
      localStorage.removeItem("auth_token");
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("fripri_logged_in", "1");
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, referralCode) => {
    const { data } = await api.post("/auth/register", { email, password, name, referral_code: referralCode || null });
    localStorage.setItem("fripri_logged_in", "1");
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("fripri_logged_in");
    localStorage.removeItem("auth_token");
    setUser(null);
  };

  const value = { user, setUser, loading, login, register, logout, checkAuth };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
